import {
  Box,
  Button,
  chakra,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ListTeamswithMembersQuery } from '@/@generated/codegen/API';
import { usePaginationState } from '@/hooks/usePaginationState';
import { useTableQueryParamsUtils } from '@/hooks/useTableQueryParamsUtils';
import { useConfirmModal } from '@/ui/ConfirmModal/ConfirmModal';
import ColumnVisibility from '@/ui/TanstackTable/Columns/ColumnVisibility';
import ActiveFilters from '@/ui/TanstackTable/Filter/ActiveFilters/ActiveFilters';
import Filters, { FilterExtraInfo } from '@/ui/TanstackTable/Filter/Filters';
import GlobalFilter from '@/ui/TanstackTable/Filter/GlobalFilter/GlobalFilter';
import { FilterType } from '@/ui/TanstackTable/Filter/NewFilterComponent';
import Pagination from '@/ui/TanstackTable/Pagination';
import TanstackTableView from '@/ui/TanstackTable/TanstackTableView';
import { fuzzyFilter } from '@/utils/helpers/fuzzyFilter';

import useAdminTeamTableStore from './AdminTeamListTableStore';
import getColumns, { AdminTeamListItemType } from './columns';
import CreateTeamForm from '../CreateTeamForm/CreateTeamForm';

type Props = {
  data: ListTeamswithMembersQuery;
  pagination: ReturnType<typeof usePaginationState>;
  handleDeleteTeam: (row: AdminTeamListItemType) => void;
  tablePrefix: string;
};

const filterExtraInfo: FilterExtraInfo<AdminTeamListItemType> = [
  {
    filterId: 'createdAt',
    filterType: FilterType.DATE,
  },
];

export default function AdminTeamListView({
  data,
  pagination,
  handleDeleteTeam,
  tablePrefix,
}: Props) {
  const { t } = useTranslation();
  const totalCount = data.listTeams.totalCount;
  const [updateTeam, setUpdateTeam] = useState<AdminTeamListItemType | null>(null);
  const { isOpen: isFormOpen, onOpen: openForm, onClose: closeForm } = useDisclosure();

  const customCloseForm = useCallback(() => {
    setUpdateTeam(null);
    closeForm();
  }, [closeForm]);

  const customOpenForm = useCallback(
    (row: AdminTeamListItemType | null) => {
      setUpdateTeam(row);
      openForm();
    },
    [openForm]
  );

  const confirmModalUtils = useConfirmModal();
  const columns = getColumns({
    t,
    onEditTeamClick: customOpenForm,
    onDeleteTeamClick: handleDeleteTeam,
    confirmModalUtils,
  });
  const {
    columnFilters,
    sorting,
    globalFilter,
    onColumnFiltersChange,
    onSortingChange,
    onGlobalFilterChange,
  } = useTableQueryParamsUtils(filterExtraInfo, tablePrefix);
  const [columnVisibility, setColumnVisibility, columnSizing, setColumnSizing] =
    useAdminTeamTableStore((state) => [
      state.columnVisibility,
      state.setColumnVisibility,
      state.columnSizing,
      state.setColumnSizing,
    ]);

  const table = useReactTable({
    data: data.listTeams.edges.map((edge) => edge.node),
    manualPagination: true,
    pageCount: Math.ceil(totalCount / pagination.paginationState.pageSize),
    state: {
      pagination: pagination.paginationState,
      columnSizing,
      columnVisibility,
      sorting,
    },
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
      setColumnVisibility(newVisibility);
    },
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
    },
    onPaginationChange: pagination.createOnPaginationChangeFunc(data.listTeams.pageInfo),
    onGlobalFilterChange: onGlobalFilterChange,
    onSortingChange: onSortingChange,
    defaultColumn: {},
    columnResizeMode: 'onEnd',
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columns: columns,
  });

  return (
    <Box maxW="full" p={{ base: 3, md: 3 }}>
      <Flex justifyContent="space-between" wrap="wrap" gap={2} alignItems="center" my={2}>
        <chakra.h3 fontSize="2xl" fontWeight="bold">
          {t('admin.teams.list.title')}
        </chakra.h3>
        <Button
          background="blue.400"
          color="white"
          size={{ base: 'sm', md: 'md' }}
          data-test="create-team-button-popUp"
          onClick={() => {
            customOpenForm(null);
          }}
        >
          {t('admin.teams.button.createNew')}
        </Button>

        <Modal isOpen={isFormOpen} onClose={customCloseForm}>
          <ModalOverlay />
          <ModalContent maxW={{ base: '95vw', md: 'md' }}>
            <ModalCloseButton />
            <ModalBody>
              <Box p="20px" gap="10px">
                <Heading size="sm" pb="5">
                  {t('admin.teams.form.title')}
                </Heading>

                <CreateTeamForm row={updateTeam} closeForm={customCloseForm} />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Flex>
      <>
        <Flex wrap="wrap" gap={2} alignItems="center" mb="4">
          <ColumnVisibility table={table} />
          <Filters
            columnFilters={columnFilters}
            setColumnFilters={onColumnFiltersChange}
            table={table}
            filterExtraInfo={filterExtraInfo}
          />
          <GlobalFilter
            globalFilter={globalFilter}
            setGlobalFilter={onGlobalFilterChange}
            maxWidth={{ base: 'none', md: '250px' }}
            flex={{ base: 1, md: 'initial' }}
            minW="150px"
          />
          <ActiveFilters
            columnFilters={columnFilters}
            removeItemFunc={(elem) => {
              onColumnFiltersChange(columnFilters.filter((f) => f.id !== elem.id));
            }}
            filterExtraInfo={filterExtraInfo}
          />
          <Pagination
            table={table}
            pageInfo={data.listTeams.pageInfo}
            totalRows={totalCount}
            tablePrefix={tablePrefix}
            ml="auto"
          />
        </Flex>

        <TanstackTableView table={table} />
      </>
    </Box>
  );
}
